import { UserPrivilege } from '@sbu/hooks.userprivileges';

import { UserPrivilege as LegacyPrivilege } from 'core/graphql';

import { ConfigPageHeaderProps } from './ConfigPageHeader';

export enum EntitiesConfigPages {
  Groups = 'groups',
  Normalization = 'normalization',
  CustomTypes = 'customTypes',
  Criticality = 'criticality',
}

export enum WorkflowConfigPages {
  Detection = 'detection',
  Statuses = 'statuses',
  Resolution = 'resolution',
  TagSchemas = 'tagSchemas',
}

export enum IntegrationsConfigPages {
  IngestMappings = 'ingestMappings',
  LogMappings = 'logMappings',
  ContextActions = 'contextActions',
  Actions = 'actions',
  Enrichment = 'enrichment',
  Automation = 'automation',
}

export type ConfigPageValue =
  | EntitiesConfigPages
  | WorkflowConfigPages
  | IntegrationsConfigPages;

export const configPagePrivilegesMap: Record<
  ConfigPageValue,
  [UserPrivilege, LegacyPrivilege]
> = {
  [EntitiesConfigPages.Groups]: [
    UserPrivilege.SiemEntityGroupsAccess,
    LegacyPrivilege.SiemEntityGroupsAccess,
  ],
  [EntitiesConfigPages.Normalization]: [
    UserPrivilege.SiemNormalizationAccess,
    LegacyPrivilege.SiemNormalizationAccess,
  ],
  [EntitiesConfigPages.CustomTypes]: [
    UserPrivilege.SiemCustomEntityTypesAccess,
    LegacyPrivilege.SiemCustomEntityTypesAccess,
  ],
  [EntitiesConfigPages.Criticality]: [
    UserPrivilege.SiemEntityCriticalityAccess,
    LegacyPrivilege.SiemEntityCriticalityAccess,
  ],

  [WorkflowConfigPages.Detection]: [
    UserPrivilege.SiemInsightDetectionAccess,
    LegacyPrivilege.SiemInsightDetectionAccess,
  ],
  [WorkflowConfigPages.Statuses]: [
    UserPrivilege.SiemInsightStatusAccess,
    LegacyPrivilege.SiemInsightStatusAccess,
  ],
  [WorkflowConfigPages.Resolution]: [
    UserPrivilege.SiemInsightResolutionAccess,
    LegacyPrivilege.SiemInsightResolutionAccess,
  ],
  [WorkflowConfigPages.TagSchemas]: [
    UserPrivilege.SiemTagSchemasAccess,
    LegacyPrivilege.SiemTagSchemasAccess,
  ],

  [IntegrationsConfigPages.IngestMappings]: [
    UserPrivilege.SiemIngestMappingsAccess,
    LegacyPrivilege.SiemIngestMappingsAccess,
  ],
  [IntegrationsConfigPages.LogMappings]: [
    UserPrivilege.SiemLogMappingsAccess,
    LegacyPrivilege.SiemLogMappingsAccess,
  ],
  [IntegrationsConfigPages.ContextActions]: [
    UserPrivilege.SiemContextActions,
    LegacyPrivilege.SiemContextActions,
  ],
  [IntegrationsConfigPages.Actions]: [
    UserPrivilege.SiemActionsAccess,
    LegacyPrivilege.SiemActionsAccess,
  ],
  [IntegrationsConfigPages.Enrichment]: [
    UserPrivilege.SiemEnrichmentsAccess,
    LegacyPrivilege.SiemEnrichmentsAccess,
  ],
  [IntegrationsConfigPages.Automation]: [
    UserPrivilege.SiemAutomationAccess,
    LegacyPrivilege.SiemAutomationAccess,
  ],
};

export const configPagePrivileges = Object.values(configPagePrivilegesMap).map(
  ([privilege, _]) => privilege
);

export const legacyConfigPagePrivileges: LegacyPrivilege[] = Object.values(
  configPagePrivilegesMap
).map(([_, legacyPrivilege]) => legacyPrivilege);

export const entitiesPageHeaderProps: Omit<
  ConfigPageHeaderProps,
  'currentPage'
> = {
  title: 'Cloud SIEM Entities Configuration',
  pages: [
    {
      label: 'Groups',
      value: EntitiesConfigPages.Groups,
      route: '/sec/config/entities/groups',
      requiredPrivileges: configPagePrivilegesMap[EntitiesConfigPages.Groups],
    },
    {
      label: 'Normalization',
      value: EntitiesConfigPages.Normalization,
      route: '/sec/config/entity-normalization/domain',
      requiredPrivileges:
        configPagePrivilegesMap[EntitiesConfigPages.Normalization],
    },
    {
      label: 'Custom Types',
      value: EntitiesConfigPages.CustomTypes,
      route: '/sec/config/incoming-data/entity-types',
      requiredPrivileges:
        configPagePrivilegesMap[EntitiesConfigPages.CustomTypes],
    },
    {
      label: 'Criticality',
      value: EntitiesConfigPages.Criticality,
      route: '/sec/config/incoming-data/entity-criticality-configs',
      requiredPrivileges:
        configPagePrivilegesMap[EntitiesConfigPages.Criticality],
    },
  ],
};

export const workflowPageHeaderProps: Omit<
  ConfigPageHeaderProps,
  'currentPage'
> = {
  title: 'Cloud SIEM Workflow Configuration',
  pages: [
    {
      route: '/sec/config/workflow/detection',
      label: 'Insight Detection',
      value: WorkflowConfigPages.Detection,
      requiredPrivileges:
        configPagePrivilegesMap[WorkflowConfigPages.Detection],
    },
    {
      route: '/sec/config/workflow/statuses',
      label: 'Insight Statuses',
      value: WorkflowConfigPages.Statuses,
      requiredPrivileges: configPagePrivilegesMap[WorkflowConfigPages.Statuses],
    },
    {
      route: '/sec/config/workflow/resolutions',
      label: 'Insight Resolutions',
      value: WorkflowConfigPages.Resolution,
      requiredPrivileges:
        configPagePrivilegesMap[WorkflowConfigPages.Resolution],
    },
    {
      route: '/sec/config/workflow/tag-schemas',
      label: 'Tag Schemas',
      value: WorkflowConfigPages.TagSchemas,
      requiredPrivileges:
        configPagePrivilegesMap[WorkflowConfigPages.TagSchemas],
    },
  ],
};

export const integrationsPageHeaderProps: Omit<
  ConfigPageHeaderProps,
  'currentPage'
> = {
  title: 'Cloud SIEM Integrations Configuration',
  pages: [
    {
      label: 'Ingest Mappings',
      route: '/sec/config/integrations/sumologic',
      value: IntegrationsConfigPages.IngestMappings,
      requiredPrivileges:
        configPagePrivilegesMap[IntegrationsConfigPages.IngestMappings],
    },
    {
      label: 'Log Mappings',
      route: '/sec/config/incoming-data/mappings',
      value: IntegrationsConfigPages.LogMappings,
      requiredPrivileges:
        configPagePrivilegesMap[IntegrationsConfigPages.LogMappings],
    },
    {
      label: 'Context Actions',
      route: '/sec/config/integrations/context-actions',
      value: IntegrationsConfigPages.ContextActions,
      requiredPrivileges:
        configPagePrivilegesMap[IntegrationsConfigPages.ContextActions],
    },
    {
      label: 'Actions',
      route: '/sec/config/integrations/actions',
      value: IntegrationsConfigPages.Actions,
      requiredPrivileges:
        configPagePrivilegesMap[IntegrationsConfigPages.Actions],
    },
    {
      label: 'Enrichment',
      route: '/sec/config/integrations/enrichment',
      value: IntegrationsConfigPages.Enrichment,
      requiredPrivileges:
        configPagePrivilegesMap[IntegrationsConfigPages.Enrichment],
    },
    {
      label: 'Automation',
      route: '/sec/config/integrations/automation',
      value: IntegrationsConfigPages.Automation,
      requiredPrivileges:
        configPagePrivilegesMap[IntegrationsConfigPages.Automation],
    },
  ],
};

export const headerPropsMap = {
  // Entities Pages
  [EntitiesConfigPages.Groups]: entitiesPageHeaderProps,
  [EntitiesConfigPages.Normalization]: entitiesPageHeaderProps,
  [EntitiesConfigPages.CustomTypes]: entitiesPageHeaderProps,
  [EntitiesConfigPages.Criticality]: entitiesPageHeaderProps,

  // Workflow Pages
  [WorkflowConfigPages.Detection]: workflowPageHeaderProps,
  [WorkflowConfigPages.Statuses]: workflowPageHeaderProps,
  [WorkflowConfigPages.Resolution]: workflowPageHeaderProps,
  [WorkflowConfigPages.TagSchemas]: workflowPageHeaderProps,

  // Integrations Pages
  [IntegrationsConfigPages.IngestMappings]: integrationsPageHeaderProps,
  [IntegrationsConfigPages.LogMappings]: integrationsPageHeaderProps,
  [IntegrationsConfigPages.ContextActions]: integrationsPageHeaderProps,
  [IntegrationsConfigPages.Actions]: integrationsPageHeaderProps,
  [IntegrationsConfigPages.Enrichment]: integrationsPageHeaderProps,
  [IntegrationsConfigPages.Automation]: integrationsPageHeaderProps,
};
